/**
 * The marking tool of the submission panel (#1170): rectangles and arrows
 * drawn onto a screenshot. Shapes are kept in the image's own pixels, so the
 * same list draws the small preview and the full-size PNG that is uploaded.
 */

export type MarkupTool = 'rect' | 'arrow'

export interface Point {
  x: number
  y: number
}

export interface MarkupShape {
  tool: MarkupTool
  from: Point
  to: Point
}

/** Shapes smaller than this (image pixels) are a stray click, not a mark. */
export const MIN_SHAPE_SIZE_PX = 6

/** The drawing surface the shapes need — a subset of `CanvasRenderingContext2D`. */
export type MarkupContext = Pick<
  CanvasRenderingContext2D,
  'beginPath' | 'moveTo' | 'lineTo' | 'stroke' | 'strokeRect' | 'save' | 'restore'
> & {
  strokeStyle: CanvasRenderingContext2D['strokeStyle']
  lineWidth: number
  lineCap: CanvasLineCap
  lineJoin: CanvasLineJoin
}

/** Line width for an image of this width: readable on a 4K capture, not crude on a crop. */
export function strokeWidthFor(imageWidth: number): number {
  return Math.max(3, Math.round(imageWidth / 400))
}

export function isMeaningfulShape(shape: MarkupShape): boolean {
  return Math.hypot(shape.to.x - shape.from.x, shape.to.y - shape.from.y) >= MIN_SHAPE_SIZE_PX
}

/** The rectangle spanned by two corners, whichever way it was dragged. */
export function normalizedRect(shape: MarkupShape): { x: number; y: number; w: number; h: number } {
  return {
    x: Math.min(shape.from.x, shape.to.x),
    y: Math.min(shape.from.y, shape.to.y),
    w: Math.abs(shape.to.x - shape.from.x),
    h: Math.abs(shape.to.y - shape.from.y),
  }
}

/** The two ends of the arrow head's strokes, at the `to` end. */
export function arrowHead(shape: MarkupShape, lineWidth: number): [Point, Point] {
  const angle = Math.atan2(shape.to.y - shape.from.y, shape.to.x - shape.from.x)
  const length = lineWidth * 5
  const spread = Math.PI / 7
  return [
    { x: shape.to.x - length * Math.cos(angle - spread), y: shape.to.y - length * Math.sin(angle - spread) },
    { x: shape.to.x - length * Math.cos(angle + spread), y: shape.to.y - length * Math.sin(angle + spread) },
  ]
}

/**
 * Draws the shapes in `color`, scaled from image pixels by `scale` (the
 * preview canvas is smaller than the image; the export uses 1).
 */
export function drawShapes(ctx: MarkupContext, shapes: MarkupShape[], color: string, lineWidth: number, scale = 1): void {
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineWidth = lineWidth * scale
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (const shape of shapes) {
    if (shape.tool === 'rect') {
      const { x, y, w, h } = normalizedRect(shape)
      ctx.strokeRect(x * scale, y * scale, w * scale, h * scale)
      continue
    }
    const [left, right] = arrowHead(shape, lineWidth)
    ctx.beginPath()
    ctx.moveTo(shape.from.x * scale, shape.from.y * scale)
    ctx.lineTo(shape.to.x * scale, shape.to.y * scale)
    ctx.moveTo(left.x * scale, left.y * scale)
    ctx.lineTo(shape.to.x * scale, shape.to.y * scale)
    ctx.lineTo(right.x * scale, right.y * scale)
    ctx.stroke()
  }
  ctx.restore()
}

/** Fallback when the theme accent cannot be resolved: the brand's signal orange. */
export const FALLBACK_MARK_COLOR = '#FFAA3A'

/**
 * The theme accent as a colour a canvas understands. The variable may hold a
 * `color-mix()` or another expression the canvas cannot parse, so the browser
 * resolves it on a probe element first.
 */
export function resolveAccentColor(root: HTMLElement = document.body): string {
  try {
    const probe = document.createElement('span')
    probe.style.color = 'var(--theme-interactive-color)'
    probe.style.display = 'none'
    root.appendChild(probe)
    const resolved = getComputedStyle(probe).color
    probe.remove()
    return resolved && resolved !== 'var(--theme-interactive-color)' ? resolved : FALLBACK_MARK_COLOR
  } catch {
    return FALLBACK_MARK_COLOR
  }
}

/** The screenshot with its marks, full size, as a PNG. */
export async function renderMarkedPng(image: HTMLImageElement, shapes: MarkupShape[], color: string): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('TICKET_MARKUP_CANVAS_UNAVAILABLE')
  ctx.drawImage(image, 0, 0)
  drawShapes(ctx, shapes, color, strokeWidthFor(image.naturalWidth))
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('TICKET_MARKUP_EXPORT_FAILED'))), 'image/png'),
  )
}

/** Decodes a screenshot for the marking view. */
export function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('TICKET_MARKUP_IMAGE_UNREADABLE'))
    }
    image.src = url
  })
}

/** A pointer position on the shown canvas, in the image's own pixels, clamped to the image. */
export function toImagePoint(
  client: Point,
  box: { left: number; top: number; width: number; height: number },
  image: { naturalWidth: number; naturalHeight: number },
): Point {
  const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max)
  return {
    x: clamp(((client.x - box.left) / (box.width || 1)) * image.naturalWidth, image.naturalWidth),
    y: clamp(((client.y - box.top) / (box.height || 1)) * image.naturalHeight, image.naturalHeight),
  }
}
